import type { TFunction } from "i18next";
import { utils, write } from "xlsx";
import { ContractTaskStatus, type PersonTaskWorkloadDto } from "../../autogen/bff-api";
import { sanitizeFilename } from "../../common/utils/sanitizeUtils";
import { urls } from "../../urls";
import { saveBlob } from "../../common/download/downloadBffFile";

export type TaskKind = "open" | "overdue" | "completedRecently";

export const TASK_KINDS: TaskKind[] = ["open", "overdue", "completedRecently"];

export const kindLabel = (t: TFunction, kind: TaskKind): string =>
  ({
    open: t("Open tasks"),
    overdue: t("Overdue tasks"),
    completedRecently: t("Completed in the last 30 days"),
  })[kind];

export const columnLabel = (t: TFunction, kind: TaskKind): string =>
  kind === "open" ? t("Open (incl. overdue)") : kindLabel(t, kind);

const nameKey = (name: string) => name.trim().toLocaleLowerCase();

export const sharedNames = (people: { name: string }[]): Set<string> => {
  const seen = new Set<string>();
  const shared = new Set<string>();
  people.forEach(({ name }) => (seen.has(nameKey(name)) ? shared.add(nameKey(name)) : seen.add(nameKey(name))));
  return shared;
};

export const sharesName = (shared: Set<string>, person: { name: string }): boolean =>
  shared.has(nameKey(person.name));

export const countOf = (person: PersonTaskWorkloadDto, kind: TaskKind): number => person[kind];

export const tasksLink = (
  person: PersonTaskWorkloadDto,
  kind: TaskKind,
  dates: { today: string; completedSince: string },
): string => {
  const query = new URLSearchParams({ person: person.personId, personName: person.name });
  if (kind === "completedRecently") {
    query.set("status", ContractTaskStatus.Completed);
    query.set("changedSince", dates.completedSince);
  } else {
    query.set("status", ContractTaskStatus.Open);
    if (kind === "overdue") query.set("dueBefore", dates.today);
  }
  return `${urls.app.contracts.tasks}?${query}`;
};

const responsibleCell = (t: TFunction, person: PersonTaskWorkloadDto): string => {
  const marks = [!person.active && t("Deactivated"), person.external && t("External")].filter(Boolean);
  return marks.length ? `${person.name} (${marks.join(", ")})` : person.name;
};

export const tasksPerOwnerSheetRows = (
  t: TFunction,
  people: PersonTaskWorkloadDto[],
  heading: { retrieved: string; scope: string },
): (string | number)[][] => [
  [String(t("Retrieved {{date}}", { date: heading.retrieved }))],
  [String(t("Based on what you have access to: {{scope}}", { scope: heading.scope }))],
  [],
  [String(t("Responsible")), String(t("Email")), ...TASK_KINDS.map((kind) => columnLabel(t, kind))],
  ...people.map((person) => [
    responsibleCell(t, person),
    person.email,
    ...TASK_KINDS.map((kind) => countOf(person, kind)),
  ]),
];

export const downloadTasksPerOwner = (
  t: TFunction,
  people: PersonTaskWorkloadDto[],
  heading: { retrieved: string; scope: string },
  fileName: string,
) => {
  const workbook = utils.book_new();
  utils.book_append_sheet(workbook, utils.aoa_to_sheet(tasksPerOwnerSheetRows(t, people, heading)), "Oppgaver");
  saveBlob(
    new Blob([write(workbook, { bookType: "xlsx", type: "buffer" })], { type: "application/octet-stream" }),
    `${sanitizeFilename(fileName)}.xlsx`,
  );
};
